/**
 * Indicator Side Panel Component
 *
 * Collapsible side panel that organizes all chart indicators into
 * logical groups. Replaces the cluttered navbar toggles.
 *
 * Groups:
 * - Options / OI: Zero Levels, Gamma Levels, Exposure Profile, Hedging Demand, Open Interest, Options Volume, Projected Curves, IV Index, IV Skew, Expected Move, Index Overlays
 * - Underlying Compare: ES / SPX / SPY per-strike lanes
 * - Volume: Volume Profile, Delta Profile, TPO Profile, VWAP, CVD
 * - Session Levels: Prior Levels, Overnight Sessions, Opening Range, Initial Balance, Opening Prints, Pivot Points, Sessions
 * - Order Flow: Absorption, Aggressive Orders, Market Structure, Rotations
 */

import { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { useAuth } from '../auth/AuthContext';
import { OVERLAY_PANEL_ENTRY } from '../indicators/underlyingOverlays';
import './IndicatorSidePanel.css';

// Admin-only group IDs (hidden from non-admin users)
const ADMIN_ONLY_GROUPS = new Set([]);

// Indicator groups configuration
const INDICATOR_GROUPS = [
  {
    id: 'options',
    label: 'Options / OI',
    color: '#a855f7',
    description: 'Open interest levels and baselines',
    indicators: [
      { id: 'zeroLevels', label: 'Zero Levels', description: 'Projected-curve zero-crossing levels (all crossings, unfiltered)',
        hasGreekToggles: true,
        greekToggles: [
          { id: 'zeroLevelsGex', label: 'Gamma', color: '#00ffc8' },
          { id: 'zeroLevelsDex', label: 'Delta', color: '#3b82f6' },
          { id: 'zeroLevelsVex', label: 'Vanna', color: '#a855f7' },
          { id: 'zeroLevelsCex', label: 'Charm', color: '#06b6d4' },
        ],
      },
      { id: 'gammaLevels', label: 'Gamma Levels', description: 'GVWAP with gamma-weighted band, plus top gamma strikes at the current bake spot (descriptive only)',
        subToggles: [
          { id: 'gammaLevelsGvwap', label: 'GVWAP', desc: ['Show the at-spot gamma-weighted average price line', 'At-spot GVWAP line shown'] },
          { id: 'gammaLevelsBand', label: 'GVWAP Band', parentId: 'gammaLevelsGvwap', desc: ['Shade the at-spot gamma-weighted stdev zone around the GVWAP', 'At-spot gamma-weighted stdev zone shaded around the GVWAP'] },
          { id: 'gammaLevelsWalls', label: 'Gamma Walls', desc: ['Show the three strikes with the largest at-spot gamma exposure (re-selected each session)', 'Top three at-spot gamma strikes shown (GW1-GW3, re-selected each session)'] },
        ],
      },
      { id: 'showExposureProfile', label: 'Exposure Profile', description: 'Per-strike exposure at the current price and remaining time',
        hasGreekToggles: true,
        hasModePicker: true, modePickerId: 'exposureProfileMode', modePickerLabel: 'Weighting',
        modeOptions: [
          { value: 'dealer', label: 'Dealer' },
          { value: 'absolute', label: 'Absolute' },
        ],
        greekToggles: [
          { id: 'expProfileGex', label: 'Gamma', color: '#3b82f6' },
          { id: 'expProfileDex', label: 'Delta', color: '#ef4444' },
          { id: 'expProfileVex', label: 'Vanna', color: '#a855f7' },
          { id: 'expProfileCex', label: 'Charm', color: '#06b6d4' },
        ],
        subToggles: [
          { id: 'showExpProfileHeatmap', label: 'Heatmap', desc: ['Swap the profile bars for a time by strike heatmap', 'Heatmap shown instead of profile bars'] },
          { id: 'showExpProfileT1', label: 'T+1 Profile', desc: ['Show next session profile at the right edge', 'Next session profile pinned right'] },
          { id: 'exposure', label: 'Exposure pane', desc: ['Add the net GEX/DEX/VEX/CEX time-series pane below the chart (follows the Weighting picker)', 'Net exposure pane shown (GEX per 1% move, DEX $ notional)'] },
        ],
      },
      { id: 'hedgingDemand', label: 'Hedging Demand', description: 'Combined dealer hedge flow per strike (gamma + vanna + charm, ES contracts)',
        subToggles: [
          { id: 'showHedgeDemandHeatmap', label: 'Heatmap', desc: ['Swap the hedge bars for a time by strike heatmap', 'Heatmap shown instead of hedge bars'] },
          { id: 'hedgeDemandRealized', label: 'Realized flow', desc: ['Use the window actual price and vol change instead of per-unit (+1pt, +1 vol pt); chip HD* applied, HD? data missing', 'Realized dS/dIV mode on (HD* chip; HD? = fallback to per-unit)'] },
        ],
      },
      { id: 'showOiDistribution', label: 'Open Interest', description: 'Raw OI distribution per strike',
        hasGreekToggles: true,
        greekToggles: [
          { id: 'oiDistCallOi', label: 'Call OI', color: '#4ade80' },
          { id: 'oiDistPutOi', label: 'Put OI', color: '#f87171' },
          { id: 'oiDistNet', label: 'Net', color: '#a3a3a3' },
        ],
      },
      { id: 'showOptionsVolume', label: 'Options Volume', description: 'Traded volume per strike for the forming candle; hover a candle to see its window',
        hasGreekToggles: true,
        greekToggles: [
          { id: 'optVolCalls', label: 'Call Vol', color: '#4ade80' },
          { id: 'optVolPuts', label: 'Put Vol', color: '#f87171' },
          { id: 'optVolNet', label: 'Net', color: '#a3a3a3' },
        ],
        subToggles: [
          { id: 'optVolCumulative', label: 'Cumulative', desc: ['Add a second histogram of day-to-date totals on its own axis', 'Cumulative day volume histogram shown (CV axis)'] },
          { id: 'optVolSplit', label: 'Buy/Sell split', desc: ['Split bars by dealer side (negated aggressor); unclassifiable volume not shown', 'Dealer perspective: green/red = dealer buys calls/puts (printed at bid), violet/blue = dealer sells (printed at ask)'] },
          { id: 'optVolFlowNet', label: 'Flow net', parentId: 'optVolSplit', desc: ['Reduce the split to one net dealer-flow bar per side, on its own scale (balanced strikes collapse, skew pops)', 'Net dealer flow: green/red = net dealer buying of calls/puts, violet/blue = net dealer selling'] },
          { id: 'optVolFlowPane', label: 'Flow pane', desc: ['Add an intraday pane of cumulative net dealer flow (calls and puts, contracts)', 'Net dealer flow pane shown (green = calls, red = puts; above zero = net dealer buying)'] },
        ],
      },
      { id: 'showProjectedCurves', label: 'Projected Curves', description: 'eSSVI-projected GEX/DEX/VEX/CEX curves, baked at expiry minus 24h (static per session; OI Data = Live refits today per minute)',
        hasGreekToggles: true,
        hasSubToggle: true, subToggleId: 'showForwardOi', subToggleLabels: ['T+1 Levels', 'T+1 Levels'], subToggleDesc: ['Show next session curves (right edge)', 'Next session curves pinned right'],
        greekToggles: [
          { id: 'projGex', label: 'GEX', color: '#3b82f6' },
          { id: 'projDex', label: 'DEX', color: '#ef4444' },
          { id: 'projVex', label: 'VEX', color: '#a855f7' },
          { id: 'projCex', label: 'CEX', color: '#06b6d4' },
        ],
      },
      {
        id: 'iv', label: 'IV Index', description: 'Implied Volatility',
        subToggles: [
          { id: 'ivStdDev', label: 'Std Dev Bands', desc: ['Show rolling ±2σ bands', 'Rolling ±2σ bands visible'] },
          { id: 'ivRealized', label: 'Realized Vol', desc: ['Show trailing ~2h realized volatility from ES candles incl. overnight/weekend gap variance (delivered vs implied; descriptive comparison only)', 'Realized-vol line visible (~2h delivered vs implied; descriptive only)'] },
        ],
      },
      {
        id: 'showIvSkew', label: 'IV Skew',
        description: 'Per-strike call/put IV dots (follows the Expiry Mode selector) on a draggable price-anchored axis; dimmed dots = held quote',
        // Derived overlay computed from the row's fitted smile: descriptive
        // only. In FULL mode it describes the FRONT expiry (labeled).
        subToggles: [
          { id: 'ivSkewBand', label: 'Distribution', desc: ['Show the risk-neutral 25-75% settlement band with median/mode (central region only; not real-world odds)', 'Risk-neutral 25-75% settlement band visible (central region; not real-world odds)'] },
          { id: 'ivSkewShape', label: 'Shape History', desc: ['Show a sub-pane with the fitted curve shape through the session: SK (ATM skew), CU (wing curvature), FIT (fit quality, vol pts)', 'Shape History pane visible (SK / CU / FIT intraday series; descriptive only)'] },
        ],
      },
      { id: 'expectedMove', label: 'Expected Move', description: 'IV-based expected move bands (1σ)' },
      // One "Index Overlays" parent row with a sub-toggle per overlay
      // (spx/spy/nq/rty/ym/zn), from the underlyingOverlays registry (SSOT).
      OVERLAY_PANEL_ENTRY,
    ],
  },
  {
    id: 'compare',
    label: 'Underlying Compare',
    color: '#14b8a6',
    description: 'All three option books at once; independent of the Underlying Option selector',
    indicators: [
      { id: 'showUnderlyingCompare', label: 'ES / SPX / SPY', description: 'Right-docked lanes: per-strike book strip aligned to the price axis, with live spot ticks (lane spread = cash-futures divergence)',
        hasModePicker: true, modePickerId: 'underlyingCompareMode', modePickerLabel: 'Fill',
        modeOptions: [
          { value: 'oi', label: 'OI' },
          { value: 'exposure', label: 'Exposure' },
          { value: 'volume', label: 'Volume' },
        ],
        hasModePicker2: true, modePickerId2: 'underlyingCompareGreek', modePickerLabel2: 'Greek',
        modePicker2When: { id: 'underlyingCompareMode', value: 'exposure' },
        modeOptions2: [
          { value: 'gex', label: 'GEX' },
          { value: 'dex', label: 'DEX' },
          { value: 'vex', label: 'VEX' },
          { value: 'cex', label: 'CEX' },
        ],
        subToggles: [
          { id: 'underlyingCompareZeroLevels', label: 'Zero Levels', desc: ['Draw each lane its own underlying zero crossings (gex/dex/vex/cex ticks, small labels)', 'Per-lane zero-crossing ticks shown (gex/dex/vex/cex)'] },
          { id: 'underlyingCompareGammaLevels', label: 'Gamma Levels', desc: ['Draw each lane its own underlying GVWAP line and GW1-GW3 walls (small labels)', 'Per-lane GVWAP and gamma walls shown'] },
        ],
      },
    ],
  },
  {
    id: 'volume',
    label: 'Volume',
    color: '#22c55e',
    description: 'Volume-based indicators',
    indicators: [
      { id: 'volumeProfile', label: 'Volume Profile', description: 'POC, VAH/VAL, CVA (core value area) histogram',
        hasModePicker: true, modePickerId: 'vpSessionMode',
        modeOptions: [
          { value: 'full', label: 'Full' },
          { value: 'split', label: 'ETH/RTH' },
        ],
        hasSubToggle: true, subToggleId: 'nakedPOC',
        subToggleLabels: ['Naked POC', 'Naked POC'],
        subToggleDesc: ['Show untested settled-session POCs', 'Untested POCs visible'],
        subToggles: [
          { id: 'hvnZones', label: 'HVN Zones',
            desc: ['Show high volume node bands (last 5 RTH sessions)', 'High volume node bands visible'] },
        ],
      },
      { id: 'deltaProfile', label: 'Delta Profile', description: 'Buy/sell imbalance per level' },
      { id: 'tpoProfile', label: 'TPO Profile', description: 'Market Profile with POC and value area (Letters mode tints IB periods A/B)',
        hasSubToggle: true, subToggleId: 'nakedTpoPOC',
        subToggleLabels: ['Naked TPO POC', 'Naked TPO POC'],
        subToggleDesc: ['Show untested settled-session TPO POCs', 'Untested TPO POCs visible'],
        hasModePicker: true, modePickerId: 'tpoRenderMode',
        modeOptions: [
          { value: 'histogram', label: 'Bars' },
          { value: 'letters', label: 'Letters' },
        ],
        hasModePicker2: true, modePickerId2: 'tpoSessionScope',
        modeOptions2: [
          { value: 'full', label: 'Full' },
          { value: 'eth-rth', label: 'ETH/RTH' },
          { value: 'rth', label: 'RTH' },
        ],
        hasModePicker3: true, modePickerId3: 'tpoDisplayStyle',
        modeOptions3: [
          { value: 'basic', label: 'Basic' },
          { value: 'split', label: 'Split' },
        ],
      },
      { id: 'vwap', label: 'VWAP', description: 'Volume weighted average price',
        hasModePicker: true, modePickerId: 'vwapSessionMode',
        modeOptions: [
          { value: 'full', label: 'Full' },
          { value: 'split', label: '+RTH' },
        ],
        subToggles: [
          { id: 'vwapBand1', label: '1st Std Dev', desc: ['Show ±1σ bands', '±1σ bands visible'] },
          { id: 'vwapBand2', label: '2nd Std Dev', desc: ['Show ±2σ bands', '±2σ bands visible'] },
        ],
      },
      { id: 'cvd', label: 'CVD', description: 'Cumulative volume delta pane' },
    ],
  },
  {
    id: 'levels',
    label: 'Session Levels',
    color: '#f59e0b',
    description: 'Session and reference levels',
    indicators: [
      {
        id: 'priorLevels', label: 'Prior Levels', description: 'Prior day / week / month levels',
        subToggles: [
          { id: 'prevDayLevels', label: 'Prior Day', desc: ['Show PDH/PDL/PDC', 'PDH/PDL/PDC visible'] },
          { id: 'priorWeekLevels', label: 'Prior Week', desc: ['Show PWH/PWL/PWC/PWO', 'Prior week levels visible'] },
          { id: 'priorMonthLevels', label: 'Prior Month', desc: ['Show PMH/PML/PMC/PMO', 'Prior month levels visible'] },
          // Prior-session value areas (data from the order flow context feed, not the greeks plugin).
          { id: 'orderflowContextProfile', label: 'Context Profile', desc: ['Show prior session profile: POC, value area, core area', 'Prior session profile visible'] },
        ],
      },
      {
        id: 'overnightLevel', label: 'Overnight Sessions', description: 'Asia/London session levels',
        subToggles: [
          { id: 'asiaLevels', label: 'Asia Session', desc: ['Show ASH/ASL (6 PM - 3 AM ET)', 'ASH/ASL visible'] },
          { id: 'londonLevels', label: 'London Session', desc: ['Show LDH/LDL (3 AM - 9:30 AM ET)', 'LDH/LDL visible'] },
        ],
      },
      { id: 'openingRange', label: 'Opening Range', description: '9:30-9:35 AM high/low/mid' },
      { id: 'initialBalance', label: 'Initial Balance', description: '9:30-10:30 AM high/low/mid' },
      { id: 'openingPrints', label: 'Opening Prints', description: 'GOP + RTHOP opening print levels (first-candle opens, 18:00 and 9:30 ET)' },
      { id: 'pivotPoints', label: 'Pivot Points', description: 'Floor pivots from prior RTH H/L/C: PP, R1-R3, S1-S3' },
      { id: 'sessionMarkers', label: 'Sessions', description: 'Globex open, London open/close, RTH open/close markers' },
    ],
  },
  {
    id: 'orderflow',
    label: 'Order Flow',
    color: '#ec4899',
    description: 'Order-flow levels',
    indicators: [
      // Order Flow Patterns
      { id: 'orderflowAbsorption', label: 'Absorption', description: 'Price bands where market orders traded into wicks and were absorbed by resting orders (buyers blue, sellers orange)' },
      { id: 'orderflowAggressiveOrders', label: 'Aggressive Orders', description: 'Whole points where big single prints landed on stacked same-side imbalances (buyers blue, sellers orange)' },
      {
        id: 'orderflowStructure', label: 'Market Structure', description: 'Swing structure: minor/intermediate/major swings (HH/HL/LH/LL), body-close break lines (BOS/CHoCH), per-level bias',
        subToggles: [
          { id: 'orderflowStructureDetail', label: 'Minor swings', desc: ['Show minor swings (dots + dotted break lines)', 'Minor swings visible'] },
        ],
      },
      {
        id: 'orderflowRotations', label: 'Rotations', description: 'Renko rotation legs + reach levels (50/70/90)',
        hasModePicker: true, modePickerId: 'orderflowRotationBrickSize', modePickerLabel: 'Brick',
        modeOptions: [{ value: 8, label: '8pt' }, { value: 4, label: '4pt' }],
        subToggles: [
          { id: 'orderflowRotationLegs', label: 'Legs', desc: ['Show the rotation zig-zag (pivots connected)', 'Rotation legs visible'] },
          { id: 'orderflowRotationReach', label: 'Reach (50/70/90)', desc: ['p50/p70/p90 reach levels per pivot + forward projection', 'Reach levels visible'] },
        ],
      },
    ],
  },
];

// Indicators that require GEX plugin async enable: disable these toggles while loading
const GREEKS_PLUGIN_INDICATORS = new Set([
  'hedgingDemand', 'showIvSkew', 'showOptionsVolume',
  'showExposureProfile', 'showOiDistribution', 'zeroLevels', 'gammaLevels', 'expectedMove',
  'priorLevels', 'prevDayLevels', 'priorWeekLevels', 'priorMonthLevels',
  'overnightLevel', 'asiaLevels', 'londonLevels',
  'openingRange', 'initialBalance', 'openingPrints', 'pivotPoints',
]);

// Keys never captured in a user-saved preset: transient tooltip UI state.
// Leading-underscore keys (one-time migration markers) are excluded by rule.
// Candle colors / opacity / display type live outside the indicators blob, so
// they are never captured to begin with.
const PRESET_EXCLUDE_KEYS = new Set(['tooltipHidden', 'tooltipPinned']);

// localStorage helpers for panel UI state (same pattern as App.jsx indicator persistence)
const loadStored = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    if (v !== null) return JSON.parse(v);
  } catch (e) { /* ignore */ }
  return fallback;
};
const store = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* ignore */ }
};

function Switch({ on, color, onClick, disabled = false, small = false }) {
  return (
    <button
      className={`isp-switch${on ? ' on' : ''}${small ? ' small' : ''}`}
      style={color ? { '--sw': color } : undefined}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="isp-knob" />
    </button>
  );
}

function Segmented({ options, value, onChange }) {
  return (
    <div className="isp-seg">
      {options.map(opt => (
        <button
          key={opt.value}
          className={value === opt.value ? 'active' : ''}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

const SearchIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <circle cx="10.5" cy="10.5" r="6.5" />
    <line x1="15.5" y1="15.5" x2="20.5" y2="20.5" />
  </svg>
);

// One-line refit coverage for the OI Data = Live state row. health is the
// live-curve row's refit field: front = today's expiry, mid = through the
// next monthly, tail = beyond; "held" slices ride settled marks because
// they lacked live quotes this minute (permanent for the full-book tail).
function liveHealthText(h) {
  if (!h) return null;
  if (!h.mid_total && !h.tail_total) {
    // 0dte book: one slice, live or held wholesale
    return h.front_ok === true ? 'live' : 'held (no live quotes)';
  }
  const parts = [
    `front: ${h.front_ok === true ? 'live' : (h.front_ok === false ? 'held' : 'n/a')}`,
  ];
  if (h.mid_total > 0) parts.push(`mid: ${h.mid_total - h.mid_held}/${h.mid_total} live`);
  if (h.tail_total > 0) {
    parts.push(h.tail_held > 0 ? `tail: settled (${h.tail_held} held)` : 'tail: live');
  }
  return parts.join(', ');
}

function IndicatorSidePanel({
  indicators = {}, onIndicatorToggle,
  onApplyIndicators,
  greeksLoading = false,
  groups = INDICATOR_GROUPS,
  storageKey = 'indicatorPanel',
  defaultExpandedGroups = ['options', 'orderflow'],
  showContext = true,
  onOpenChange,
  liveCurvesHealth = null,
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [isOpen, setIsOpenState] = useState(() => loadStored(`${storageKey}Open`, false) === true);
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const v = loadStored(`${storageKey}Groups`, null);
    return Array.isArray(v) ? v : defaultExpandedGroups; // Default expanded
  });
  const [query, setQuery] = useState('');

  // Hover popout for indicator descriptions: one shared viewport-fixed card
  // floating left of the panel (rows sit inside overflow-hidden collapse
  // containers, so it cannot be positioned within the row itself).
  const [tip, setTip] = useState(null); // { text, y } with y = hovered row's vertical center
  const tipRef = useRef(null);
  const showTip = (e, text) => {
    if (!text) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTip({ text, y: rect.top + rect.height / 2 });
  };
  const hideTip = () => setTip(null);

  // Clamp the popout inside the viewport (it is translateY(-50%)-centered on
  // tip.y, so long descriptions near the panel bottom would otherwise spill).
  useLayoutEffect(() => {
    if (!tip || !tipRef.current) return;
    const h = tipRef.current.offsetHeight;
    const min = 8 + h / 2;
    const max = window.innerHeight - 8 - h / 2;
    const y = Math.min(Math.max(tip.y, min), max);
    if (y !== tip.y) setTip((t) => (t ? { ...t, y } : t));
  }, [tip]);

  const setIsOpen = (open) => {
    setIsOpenState(open);
    store(`${storageKey}Open`, open);
  };

  // Notify parent of open state (mount + toggle) so it can reserve chart space
  useEffect(() => { onOpenChange?.(isOpen); }, [isOpen]);

  // Filter groups and indicators based on user role
  const visibleGroups = useMemo(() => {
    if (isAdmin) return groups;
    return groups.filter(group => !ADMIN_ONLY_GROUPS.has(group.id));
  }, [isAdmin, groups]);

  // User-saved named presets (per page via storageKey). Each entry snapshots
  // the indicators blob at save time: enables, sub/greek toggles, mode pickers,
  // axis anchor fracs and the context selectors (Expiry Mode / Underlying / OI
  // Data). Snapshots are per browser+user, so no admin filtering is needed.
  const [savedPresets, setSavedPresets] = useState(() => {
    const v = loadStored(`${storageKey}SavedPresets`, null);
    return Array.isArray(v) ? v : [];
  });
  const [presetName, setPresetName] = useState('');

  // Search filter: narrow indicators by label/description, drop empty groups
  const q = query.trim().toLowerCase();
  const searching = q.length > 0;
  const filteredGroups = useMemo(() => {
    if (!searching) return visibleGroups;
    return visibleGroups
      .map(group => ({
        ...group,
        indicators: group.indicators.filter(ind =>
          ind.label.toLowerCase().includes(q) ||
          ind.description.toLowerCase().includes(q)
        ),
      }))
      .filter(group => group.indicators.length > 0);
  }, [visibleGroups, q, searching]);

  // Total enabled indicators across visible groups
  const activeCount = useMemo(() => {
    const ids = new Set();
    visibleGroups.forEach(group => {
      group.indicators.forEach(ind => {
        if (indicators[ind.id]) ids.add(ind.id);
      });
    });
    return ids.size;
  }, [visibleGroups, indicators]);

  // Toggle group expansion
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => {
      const next = prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId];
      store(`${storageKey}Groups`, next);
      return next;
    });
  };

  // Count enabled indicators per group
  const getGroupEnabledCount = (group) => {
    return group.indicators.filter(ind => indicators[ind.id]).length;
  };

  // Save the current setup under a name; an existing name (case-insensitive)
  // is overwritten in place.
  const savePreset = () => {
    const name = presetName.trim();
    if (!name) return;
    const snapshot = {};
    Object.entries(indicators).forEach(([key, value]) => {
      if (key.startsWith('_') || PRESET_EXCLUDE_KEYS.has(key)) return;
      snapshot[key] = value;
    });
    setSavedPresets(prev => {
      const next = prev.filter(p => p.name.toLowerCase() !== name.toLowerCase());
      next.push({ name, snapshot });
      store(`${storageKey}SavedPresets`, next);
      return next;
    });
    setPresetName('');
  };

  const deletePreset = (name) => {
    if (!window.confirm(`Delete preset "${name}"?`)) return;
    setSavedPresets(prev => {
      const next = prev.filter(p => p.name !== name);
      store(`${storageKey}SavedPresets`, next);
      return next;
    });
  };

  // Turn off every enabled indicator (sub-toggle children keep their values,
  // matching the old Clear All preset behavior).
  const clearAll = () => {
    visibleGroups.forEach(group => {
      group.indicators.forEach(ind => {
        if (indicators[ind.id]) {
          onIndicatorToggle?.(ind.id, false);
        }
      });
    });
  };

  return (
    <>
      {/* Edge tab (always visible) */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="isp-tab"
        style={{ right: isOpen ? '300px' : '0' }}
        title={isOpen ? 'Close panel' : 'Open indicators'}
      >
        <span className="isp-tab-chevron">{isOpen ? '›' : '‹'}</span>
        <span className="isp-tab-label">Indicators</span>
        {!isOpen && activeCount > 0 && (
          <span className="isp-tab-badge">{activeCount}</span>
        )}
      </button>

      {/* Side Panel */}
      <div className={`isp-panel${isOpen ? '' : ' closed'}`}>
        {/* Header */}
        <div className="isp-header">
          <div className="isp-header-row">
            <h3 className="isp-title">
              Indicators
              {activeCount > 0 && <span className="isp-title-badge">{activeCount} on</span>}
            </h3>
            <button onClick={() => setIsOpen(false)} className="isp-close">×</button>
          </div>
          <div className="isp-search">
            <span className="isp-search-icon"><SearchIcon /></span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search indicators…"
            />
            {searching && (
              <button className="isp-search-clear" onClick={() => setQuery('')}>×</button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="isp-content" onScroll={hideTip}>
          {/* Global context: expiry mode + underlying */}
          {showContext && (
            <div className="isp-ctx">
              <div className="isp-ctx-row">
                <span className="isp-ctx-label">Expiry Mode</span>
                <Segmented
                  options={[{ value: '0dte', label: '0DTE' }, { value: 'full', label: 'FULL' }]}
                  value={indicators.expiryMode}
                  onChange={(v) => onIndicatorToggle?.('expiryMode', v)}
                />
              </div>
              <div className="isp-ctx-row">
                <span className="isp-ctx-label">Underlying Option</span>
                <Segmented
                  options={[{ value: 'spx', label: 'SPX' }, { value: 'spy', label: 'SPY' }, { value: 'es', label: 'ES' }]}
                  value={indicators.underlying}
                  onChange={(v) => onIndicatorToggle?.('underlying', v)}
                />
              </div>
              {/* Global live-variant selector: applies to Zero Levels flips,
                  Projected Curves, Exposure pane and Exposure Profile.
                  Settled = T-1 settlement bake; Live = per-minute refit smile. */}
              <div className="isp-ctx-row">
                <span className="isp-ctx-label">OI Data</span>
                <Segmented
                  options={[{ value: 'settled', label: 'Settled' }, { value: 'live', label: 'Live' }]}
                  value={indicators.oiData ?? 'settled'}
                  onChange={(v) => onIndicatorToggle?.('oiData', v)}
                />
              </div>
              {/* Refit-coverage state line: which horizons of the live
                  smile actually refit this minute vs ride settled marks
                  (absent until a post-deploy live row arrives). */}
              {(indicators.oiData ?? 'settled') === 'live' && liveCurvesHealth && (
                <div className="isp-ctx-row">
                  <span className="isp-ctx-label">Live Coverage</span>
                  <span style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'right' }}>
                    {liveHealthText(liveCurvesHealth)}
                  </span>
                </div>
              )}
            </div>
          )}

          {searching && filteredGroups.length === 0 && (
            <div className="isp-empty">No indicators match &ldquo;{query.trim()}&rdquo;</div>
          )}

          {filteredGroups.map(group => {
            const isExpanded = searching || expandedGroups.includes(group.id);
            const enabledCount = getGroupEnabledCount(group);

            return (
              <div
                key={group.id}
                className={`isp-group${isExpanded ? ' open' : ''}`}
                style={{ '--g': group.color }}
              >
                {/* Group Header */}
                <div
                  className="isp-group-header"
                  onClick={() => { if (!searching) toggleGroup(group.id); }}
                >
                  <div className="isp-group-label">
                    <span className="isp-dot" />
                    <span className="isp-group-name">{group.label}</span>
                  </div>
                  <div className="isp-group-meta">
                    {enabledCount > 0 && (
                      <span className="isp-count">{enabledCount}</span>
                    )}
                    <span className="isp-chevron">›</span>
                  </div>
                </div>

                {/* Group Content (always rendered for animated expand/collapse) */}
                <div className="isp-body">
                  <div className="isp-body-inner">
                    <div className="isp-body-pad">
                      {group.indicators.map((ind) => {
                        const isEnabled = indicators[ind.id] ?? false;
                        const isLoading = greeksLoading && GREEKS_PLUGIN_INDICATORS.has(ind.id);
                        const hasSubControls = isEnabled && (
                          ind.hasGreekToggles || ind.hasModePicker || ind.hasModePicker2 ||
                          ind.hasModePicker3 || ind.hasSubToggle || ind.subToggles
                        );

                        return (
                          <div key={ind.id}>
                            <div className="isp-row">
                              <div
                                className="isp-row-info"
                                onMouseEnter={(e) => showTip(e, ind.description)}
                                onMouseLeave={hideTip}
                              >
                                <div className="isp-row-label">
                                  {ind.label}
                                  {isLoading && <span className="isp-loading">loading…</span>}
                                </div>
                              </div>
                              <Switch
                                on={isEnabled}
                                color={group.color}
                                disabled={isLoading}
                                onClick={() => !isLoading && onIndicatorToggle?.(ind.id, !isEnabled)}
                              />
                            </div>

                            {hasSubControls && (
                              <div className="isp-sub">
                                {/* Per-Greek visibility toggles */}
                                {ind.hasGreekToggles && ind.greekToggles.map((gt) => (
                                  <div key={gt.id} className="isp-row">
                                    <div className="isp-row-info">
                                      <div className="isp-row-label" style={{ color: gt.color }}>
                                        {gt.label}
                                      </div>
                                    </div>
                                    <Switch
                                      small
                                      on={indicators[gt.id] ?? true}
                                      color={gt.color}
                                      onClick={() => onIndicatorToggle?.(gt.id, !(indicators[gt.id] ?? true))}
                                    />
                                  </div>
                                ))}
                                {/* Mode pickers (segmented controls) */}
                                {ind.hasModePicker && (
                                  <div className="isp-row">
                                    <div className="isp-row-info">
                                      <div className="isp-row-label">{ind.modePickerLabel || 'Mode'}</div>
                                    </div>
                                    <Segmented
                                      options={ind.modeOptions}
                                      value={indicators[ind.modePickerId]}
                                      onChange={(v) => onIndicatorToggle?.(ind.modePickerId, v)}
                                    />
                                  </div>
                                )}
                                {ind.hasModePicker2
                                  && (!ind.modePicker2When
                                    || indicators[ind.modePicker2When.id] === ind.modePicker2When.value) && (
                                  <div className="isp-row">
                                    <div className="isp-row-info">
                                      <div className="isp-row-label">{ind.modePickerLabel2 || 'Session'}</div>
                                    </div>
                                    <Segmented
                                      options={ind.modeOptions2}
                                      value={indicators[ind.modePickerId2]}
                                      onChange={(v) => onIndicatorToggle?.(ind.modePickerId2, v)}
                                    />
                                  </div>
                                )}
                                {ind.hasModePicker3 && (
                                  <div className="isp-row">
                                    <div className="isp-row-info">
                                      <div className="isp-row-label">Display</div>
                                    </div>
                                    <Segmented
                                      options={ind.modeOptions3}
                                      value={indicators[ind.modePickerId3]}
                                      onChange={(v) => onIndicatorToggle?.(ind.modePickerId3, v)}
                                    />
                                  </div>
                                )}
                                {/* Single sub-toggle */}
                                {ind.hasSubToggle && (
                                  <div className="isp-row">
                                    <div
                                      className="isp-row-info"
                                      onMouseEnter={(e) => showTip(e, ind.subToggleDesc
                                        ? (indicators[ind.subToggleId] ? ind.subToggleDesc[1] : ind.subToggleDesc[0])
                                        : (indicators[ind.subToggleId] ? 'Intraday flow only' : 'Open interest + flow'))}
                                      onMouseLeave={hideTip}
                                    >
                                      <div className="isp-row-label">
                                        {indicators[ind.subToggleId] ? ind.subToggleLabels[1] : ind.subToggleLabels[0]}
                                      </div>
                                    </div>
                                    <Switch
                                      small
                                      on={!!indicators[ind.subToggleId]}
                                      color="#f59e0b"
                                      onClick={() => onIndicatorToggle?.(ind.subToggleId, !indicators[ind.subToggleId])}
                                    />
                                  </div>
                                )}
                                {/* Multiple sub-toggles (e.g., VWAP bands). An entry
                                    with parentId renders only while that sibling
                                    toggle is on (nested sub-toggle, indented). */}
                                {ind.subToggles && ind.subToggles
                                  .filter((sub) => !sub.parentId || indicators[sub.parentId])
                                  .map((sub) => (
                                  <div key={sub.id} className="isp-row" style={sub.parentId ? { paddingLeft: 14 } : undefined}>
                                    <div
                                      className="isp-row-info"
                                      onMouseEnter={(e) => showTip(e, indicators[sub.id] ? sub.desc[1] : sub.desc[0])}
                                      onMouseLeave={hideTip}
                                    >
                                      <div className="isp-row-label">{sub.label}</div>
                                    </div>
                                    <Switch
                                      small
                                      on={!!indicators[sub.id]}
                                      color="#4a9eff"
                                      onClick={() => onIndicatorToggle?.(sub.id, !indicators[sub.id])}
                                    />
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Description popout (viewport-fixed, left of the panel) */}
        {isOpen && tip && (
          <div ref={tipRef} className="isp-popout" style={{ top: tip.y }}>
            {tip.text}
          </div>
        )}

        {/* User-saved presets */}
        <div className="isp-presets">
          <div className="isp-presets-title">My Presets</div>
          <div className="isp-preset-save">
            <input
              type="text"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') savePreset(); }}
              placeholder="Save current setup as…"
              maxLength={40}
            />
            <button
              className="isp-chip"
              onClick={savePreset}
              disabled={!presetName.trim()}
            >
              Save
            </button>
          </div>
          <div className="isp-chips">
            {savedPresets.length === 0 && (
              <span className="isp-presets-empty">No saved presets yet</span>
            )}
            {savedPresets.map(preset => (
              <button
                key={preset.name}
                onClick={() => onApplyIndicators?.(preset.snapshot)}
                className="isp-chip"
                title={`Apply "${preset.name}"`}
              >
                {preset.name}
                <span
                  className="isp-chip-x"
                  title={`Delete "${preset.name}"`}
                  onClick={(e) => { e.stopPropagation(); deletePreset(preset.name); }}
                >
                  ×
                </span>
              </button>
            ))}
            <button onClick={clearAll} className="isp-chip danger">
              Clear All
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default IndicatorSidePanel;
export { INDICATOR_GROUPS };
